'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useWallets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/app-dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { ArrowDown01Icon, ArrowUpRight01Icon, Clock01Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { walletDisplayLabel } from '@/components/wallet/wallet-display-label';
import { FAUCET_CLAIM_AMOUNT_USD, FAUCET_GRANT_BY_CURRENCY } from '@/config/faucet';
import { D6_SCALE } from '@/config/solana';
import { type FaucetClaimFailure, type FaucetClaimResult, useFaucetClaim } from '@/hooks/faucet/use-faucet-claim';
import { getCurrencyByCode, resolveLocalAmount, useFormattedCurrency } from '@/lib/currency';
import { cooldownDisplay } from '@/lib/faucet/cooldown';
import { devnetExplorerTxUrl } from '@/lib/faucet/explorer';
import { useNetworkMode } from '@/lib/network/use-network-mode';
import { cn } from '@/lib/ui/utils';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useLayoutStore } from '@/stores/layout';
import type { Wallet } from '@/types/auth';
type FaucetStatus = 'idle' | 'cooldown' | 'success';
interface WalletClaimState {
  status: FaucetStatus;
  cooldownSeconds: number | null;
  result: FaucetClaimResult | null;
}
const IDLE_CLAIM_STATE: WalletClaimState = {
  status: 'idle',
  cooldownSeconds: null,
  result: null
};

/**
 * Devnet faucet modal — claim free test cash to a chosen wallet.
 *
 * Mounted globally in `AppShell`, driven by `useLayoutStore.faucetModalOpen`
 * (opened from the menu's devnet-only Faucet row). The outer gate keeps the
 * expensive hooks (wallet caches, claim mutation) out of the render path until
 * the modal is actually open on devnet — which also gives the inner a fresh
 * state machine on every open, so nothing stale survives a close/reopen.
 */
export function FaucetModal() {
  const $ = _c(1);
  const open = useLayoutStore(_temp);
  const networkMode = useNetworkMode();
  if (!open || networkMode === "mainnet") {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <FaucetModalInner />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Follows the canonical SilkSheet modal shape (visible title + description
 * header, actions in a `ModalFooter`, centered body) used by the other detached
 * modals. Reuses `useFaucetClaim`, so the loading / success / cooldown
 * snackbars fire here too; the modal mirrors the outcome inline via the hook
 * callbacks.
 */
function _temp(s) {
  return s.faucetModalOpen;
}
function FaucetModalInner() {
  const $ = _c(26);
  const t = useTranslations("faucet");
  const setOpen = useLayoutStore(_temp2);
  const {
    formatBalance,
    formatQuickAmount,
    currency,
    exchangeRate,
    toUsdc
  } = useFormattedCurrency();
  const {
    wallets
  } = useWallets();
  const {
    activeWalletId
  } = useActiveWallet();
  const {
    enabled: multiWalletEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  const {
    claim,
    isPending
  } = useFaucetClaim("faucet_modal");
  const [selectedId, setSelectedId] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [claimStateByAddress, setClaimStateByAddress] = useState(t0);
  let t1;
  if ($[1] !== activeWalletId || $[2] !== claim || $[3] !== claimStateByAddress || $[4] !== currency || $[5] !== exchangeRate || $[6] !== formatBalance || $[7] !== formatQuickAmount || $[8] !== isPending || $[9] !== multiWalletEnabled || $[10] !== primaryWallet || $[11] !== selectedId || $[12] !== setOpen || $[13] !== t || $[14] !== toUsdc || $[15] !== wallets) {
    const scopedWallets = multiWalletEnabled ? wallets : primaryWallet === null ? [] : [primaryWallet];
    const activeWallets = scopedWallets.filter(_temp3);
    const selectedWallet = multiWalletEnabled ? (selectedId === null ? activeWallets.find(wallet_0 => wallet_0.id === activeWalletId) : activeWallets.find(wallet_1 => wallet_1.id === selectedId)) ?? null : activeWallets[0] ?? null;
    const claimState = selectedWallet === null ? IDLE_CLAIM_STATE : claimStateByAddress[selectedWallet.address] ?? IDLE_CLAIM_STATE;
    let t2;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = function selectWallet(id) {
        setSelectedId(id);
      };
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    const selectWallet = t2;
    const handleClaim = function handleClaim() {
      if (selectedWallet === null) {
        return;
      }
      const walletAddress = selectedWallet.address;
      setClaimStateByAddress(current => ({
        ...current,
        [walletAddress]: IDLE_CLAIM_STATE
      }));
      claim(walletAddress, {
        amountUsdc: grantUsdc,
        onClaimed: claimed => {
          setClaimStateByAddress(current_0 => ({
            ...current_0,
            [walletAddress]: {
              status: "success",
              cooldownSeconds: null,
              result: claimed
            }
          }));
        },
        onFailed: failure => {
          setClaimStateByAddress(current_1 => ({
            ...current_1,
            [walletAddress]: failure.isCooldown ? {
              status: "cooldown",
              cooldownSeconds: failure.cooldownSeconds,
              result: null
            } : IDLE_CLAIM_STATE
          }));
        }
      });
    };
    let t3;
    if ($[18] !== t) {
      t3 = function cooldownText(seconds) {
        const display = cooldownDisplay(seconds);
        if (display.unit === "hours") {
          return t("cooldown_hours", {
            hours: display.value
          });
        }
        if (display.unit === "minutes") {
          return t("cooldown_minutes", {
            minutes: display.value
          });
        }
        return t("cooldown_soon");
      };
      $[18] = t;
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    const cooldownText = t3;
    const result = claimState.status === "success" ? claimState.result : null;
    const grantLocal = resolveLocalAmount(FAUCET_GRANT_BY_CURRENCY, FAUCET_CLAIM_AMOUNT_USD, currency, exchangeRate, getCurrencyByCode(currency)?.decimals ?? 2);
    const grantAmount = formatQuickAmount(grantLocal);
    const grantUsdc = Math.round(toUsdc(grantLocal) * D6_SCALE) / D6_SCALE;
    const claimDisabled = isPending || claimState.status === "cooldown" || selectedWallet === null;
    const footer = result !== null ? <ModalFooter><AnimatedButton variant="secondary" prefix={ArrowUpRight01Icon} onClick={() => {
        window.open(devnetExplorerTxUrl(result.signature), "_blank", "noopener");
      }}>{t("view_on_explorer")}</AnimatedButton><AnimatedButton onClick={() => setOpen(false)}>{t("done")}</AnimatedButton></ModalFooter> : <ModalFooter><AnimatedButton disabled={claimDisabled} loading={isPending} onClick={handleClaim}>{isPending ? t("claiming") : t("claim_cta", {
          amount: grantAmount
        })}</AnimatedButton></ModalFooter>;
    let t4;
    if ($[20] !== result || $[21] !== t) {
      t4 = result !== null ? t("success_title") : t("modal_title");
      $[20] = result;
      $[21] = t;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    let t5;
    if ($[23] !== result || $[24] !== t) {
      t5 = result !== null ? t("success_modal_subtitle") : t("modal_subtitle");
      $[23] = result;
      $[24] = t;
      $[25] = t5;
    } else {
      t5 = $[25];
    }
    t1 = <SilkSheet open={true} onOpenChange={setOpen} variant="detached" size="default" title={t4} description={t5} footer={footer}>{result !== null ? <div className="flex flex-col items-center gap-3 py-4" aria-live="polite"><div className="grid size-16 place-items-center rounded-full bg-feedback-success-surface text-feedback-success-content"><Icon icon={Tick02Icon} className="size-8" aria-hidden="true" /></div><span className="text-display-sm font-bold tabular-nums"><AnimatedNumber value={result.amount} format={v => formatBalance(v, "clean")} animateOnMount={true} /></span></div> : <div className="flex flex-col items-center gap-4 py-2"><div className="flex w-full items-center justify-between gap-3 rounded-panel-row border bg-background-secondary px-4 py-3.5"><div className="flex flex-col"><span className="text-caption text-foreground-secondary">{t("receive_label")}</span><span className="text-display-sm font-bold tabular-nums">{grantAmount}</span></div><span className="rounded-pill bg-background-tertiary px-3 py-1 text-caption font-medium text-foreground-secondary">{t("amount_tag")}</span></div>{selectedWallet === null ? <div className="flex w-full flex-col rounded-panel-inset border px-3 py-2.5 text-body font-medium text-foreground-secondary">{t("select_wallet")}</div> : activeWallets.length > 1 ? <DropdownMenu><DropdownMenuTrigger asChild={true}><button type="button" className="flex w-full items-center gap-3 rounded-panel-inset border px-3 py-2.5 text-left transition-colors duration-fast hover:bg-background-secondary"><div className="flex min-w-0 flex-col"><span className="text-caption text-foreground-tertiary">{t("deposit_to")}</span><span className="truncate text-body font-medium">{walletDisplayLabel(selectedWallet)}</span></div><Icon icon={ArrowDown01Icon} className="ml-auto size-4 shrink-0 text-foreground-tertiary" aria-hidden="true" /></button></DropdownMenuTrigger><DropdownMenuContent align="start" className="min-w-56">{activeWallets.map(w => <DropdownMenuItem key={w.id} onSelect={() => selectWallet(w.id)} className="gap-2"><Icon icon={Tick02Icon} className={cn("size-4 shrink-0", w.id === selectedWallet.id ? "opacity-100" : "opacity-0")} aria-hidden="true" /><span className="truncate">{walletDisplayLabel(w)}</span></DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu> : <div className="flex w-full flex-col rounded-panel-inset border px-3 py-2.5"><span className="text-caption text-foreground-tertiary">{t("deposit_to")}</span><span className="truncate text-body font-medium">{walletDisplayLabel(selectedWallet)}</span></div>}{claimState.status === "cooldown" && <div className="flex w-full items-center gap-2.5 rounded-panel-inset bg-background-tertiary px-3.5 py-3 text-body font-medium text-foreground-secondary"><Icon icon={Clock01Icon} className="size-4 shrink-0" aria-hidden="true" />{cooldownText(claimState.cooldownSeconds)}</div>}</div>}</SilkSheet>;
    $[1] = activeWalletId;
    $[2] = claim;
    $[3] = claimStateByAddress;
    $[4] = currency;
    $[5] = exchangeRate;
    $[6] = formatBalance;
    $[7] = formatQuickAmount;
    $[8] = isPending;
    $[9] = multiWalletEnabled;
    $[10] = primaryWallet;
    $[11] = selectedId;
    $[12] = setOpen;
    $[13] = t;
    $[14] = toUsdc;
    $[15] = wallets;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  return t1;
}
function _temp3(wallet) {
  return wallet.archivedAt === null && wallet.isAvailable !== false;
}
function _temp2(s) {
  return s.setFaucetModalOpen;
}